import React, { useContext } from 'react';
import TapeFeaturePanel from './TapeFeaturePanel';
import TapeCanvas from './TapeCanvas';
import AerialCanvas from './AerialCanvas';
import { SketchToolContext } from '../context';
import {
  updateTapeFeature,
  deleteTapeFeature,
  setSelectedTapeFeatureId,
  createTapeFeature,
  replaceTapeFeatures,
  undoTapeFeatures,
  redoTapeFeatures,
  pushTapeFeaturesHistory,
  shiftTapeSketchOrigin,
  setTapeSketchBearing,
} from '../actions';

const buildDefaultDwelling = (tapeConfig, vertices) => {
  const defaultDwelling = (tapeConfig?.featureTypes || []).find(x => x.code === 'DWL');
  const defaultFoundation = (tapeConfig?.foundationTypes || []).find(x => x.code === 'S');
  return {
    tapeFeatureTypeId: defaultDwelling?.id ?? tapeConfig?.featureTypes?.[0]?.id,
    tapeFoundationTypeId: defaultFoundation?.id ?? tapeConfig?.foundationTypes?.[0]?.id,
    tapeGarageKindId: null,
    stories: 1,
    storiesAbove: null,
    isCathedral: false,
    vertices,
  };
};

export const ConnectedTapeCanvas = () => {
  const {
    state: { tapeFeatures, selectedTapeFeatureId, tapeConfig },
    dispatch,
  } = useContext(SketchToolContext);

  return (
    <TapeCanvas
      features={tapeFeatures}
      selectedId={selectedTapeFeatureId}
      tapeConfig={tapeConfig}
      onCommit={vertices => dispatch(createTapeFeature(buildDefaultDwelling(tapeConfig, vertices)))}
      onSelect={id => dispatch(setSelectedTapeFeatureId(id))}
      onMove={(id, vertices) => dispatch(updateTapeFeature(id, 'vertices', vertices))}
      onClearFeatures={() => dispatch(replaceTapeFeatures([]))}
      onDelete={id => dispatch(deleteTapeFeature(id))}
      onUndoFeatures={() => dispatch(undoTapeFeatures())}
      onRedoFeatures={() => dispatch(redoTapeFeatures())}
      onMoveStart={() => dispatch(pushTapeFeaturesHistory())}
    />
  );
};

export const ConnectedAerialCanvas = ({ center, zoomLevel }) => {
  const {
    state: { tapeFeatures, selectedTapeFeatureId, tapeConfig, tapeSketch },
    dispatch,
  } = useContext(SketchToolContext);

  const origin =
    tapeSketch?.originLat != null && tapeSketch?.originLng != null ? { lat: tapeSketch.originLat, lng: tapeSketch.originLng } : center;

  return (
    <AerialCanvas
      center={center}
      origin={origin}
      zoomLevel={zoomLevel}
      initialBearing={tapeSketch?.bearing || 0}
      features={tapeFeatures}
      selectedId={selectedTapeFeatureId}
      tapeConfig={tapeConfig}
      onCommit={vertices => dispatch(createTapeFeature(buildDefaultDwelling(tapeConfig, vertices)))}
      onSelect={id => dispatch(setSelectedTapeFeatureId(id))}
      onMove={(id, vertices) => dispatch(updateTapeFeature(id, 'vertices', vertices))}
      onClearFeatures={() => dispatch(replaceTapeFeatures([]))}
      onDelete={id => dispatch(deleteTapeFeature(id))}
      onUndoFeatures={() => dispatch(undoTapeFeatures())}
      onRedoFeatures={() => dispatch(redoTapeFeatures())}
      onMoveStart={() => dispatch(pushTapeFeaturesHistory())}
      onShiftAll={(deltaLat, deltaLng) => dispatch(shiftTapeSketchOrigin(deltaLat, deltaLng))}
      onBearingChange={bearing => dispatch(setTapeSketchBearing(bearing))}
    />
  );
};

export default () => {
  const {
    state: { tapeFeatures, selectedTapeFeatureId, tapeConfig },
    dispatch,
  } = useContext(SketchToolContext);

  return (
    <TapeFeaturePanel
      features={tapeFeatures}
      selectedId={selectedTapeFeatureId}
      tapeConfig={tapeConfig}
      onSelect={id => dispatch(setSelectedTapeFeatureId(id === selectedTapeFeatureId ? null : id))}
      onPatch={(id, partial) => {
        // One history entry per patch (e.g. changing Type), not one per field it happens to
        // touch — a single field change like "Stories" still dispatches one updateTapeFeature
        // call, but changing Type can cascade into three (type + garage kind + storiesAbove).
        dispatch(pushTapeFeaturesHistory());
        Object.entries(partial).forEach(([key, val]) => dispatch(updateTapeFeature(id, key, val)));
      }}
      onRemove={id => dispatch(deleteTapeFeature(id))}
    />
  );
};
